import type { EnsurePackageBundledContext } from '@ms-cloudpack/api-server';
import type { PackageImporterContext } from '@ms-cloudpack/common-types';
import type { InitOptions } from './types/InitOptions.js';

import { createPartialApiContext } from '@ms-cloudpack/api-server';
import { writeGeneratedConfig } from '@ms-cloudpack/config';
import type { InitSummaryData } from './InitSummaryData.js';
import { evaluatePath } from './evaluatePath.js';
import { verifyExports } from './verifyExports.js';
import type { AppCommandContext, CommandActionParams } from '../../types/CommandAction.js';
import path from 'path';
import { writeJson } from '@ms-cloudpack/json-utilities';
import { runPrerequisites } from '../../utilities/runPrerequisites.js';
import { getFilteredPackages } from '../../utilities/getFilteredPackages.js';
import { optimizeDependencies } from './optimizeDependencies.js';
import { bulletedList } from '@ms-cloudpack/task-reporter';
import type { InitAppOptions } from './types/InitAppOptions.js';
import { SequentialPromiseExecutor } from '../../utilities/sequentialPromiseExecutor.js';

/**
 * Init execution function.
 */
export async function init(
  params: Pick<CommandActionParams<InitOptions>, keyof AppCommandContext | 'options' | 'isMultiApp'> & {
    /**
     * App summary object, created ahead of time so that a partial summary can be generated
     * if the process is interrupted.
     */
    summaryData: InitSummaryData;
    /**
     * Skip writing the generated config to disk. Useful when init is run
     * inline before start, since the in-memory config is all that's needed.
     */
    skipWrite?: boolean;
  },
): Promise<void> {
  const { options: initOptions, summaryData, isMultiApp, config, telemetryClient, skipWrite } = params;

  await telemetryClient.tracer.startActiveSpan('INIT_APP', async (span) => {
    // Run prerequisites for the app.
    await runPrerequisites(params);

    // If --reset is specified, reset the generated config.
    if (initOptions.reset) {
      config.generated = {};
    }

    const context = await createPartialApiContext(params);

    await initApp({ span, summaryData, initOptions, isMultiApp, skipWrite }, context);
  });
}

async function initApp(options: InitAppOptions, context: EnsurePackageBundledContext): Promise<void> {
  const { initOptions, summaryData, span, isMultiApp } = options;
  const { match } = initOptions;
  const { config, resolveMap } = context.session;
  const { appPath } = config;
  const executor = new SequentialPromiseExecutor();

  // Ensure config.generated.packageSettings is initialized.
  config.generated.packageSettings ??= [];

  let isExitingPrematurely = false;

  await new Promise<void>((resolve) => {
    let pending = 0;
    const visitedSet = new Set<string>();
    const refreshPaths = new Set<string>();
    const priorityMap = new Map<string, number>();

    // Start with the app path or requested matches
    const initialPaths = match ? getFilteredPackages(resolveMap, match) : [appPath];
    if (!initialPaths.length) {
      summaryData.addErrors(appPath, [{ text: `No packages found matching ${JSON.stringify(match)}`, source: 'init' }]);
      resolve();
    }

    enqueuePaths(new Map(initialPaths.map((packagePath) => [packagePath, undefined])));

    /**
     * Enqueue more paths.
     * @param newPaths Mapping from package path to importer context.
     */
    function enqueuePaths(newPaths: Map<string, PackageImporterContext | undefined>): void {
      if (isExitingPrematurely) {
        return;
      }

      // Iterate through the given paths.
      for (const [packagePath, importerContext] of newPaths) {
        if (visitedSet.has(packagePath)) {
          console.debug(`Skipping ${packagePath} as it has already been visited.`);
          continue;
        }

        visitedSet.add(packagePath);
        pending++;

        const shouldForce = refreshPaths.delete(packagePath);

        // Get the priority value for this package path, defaults to 0.
        const priority = priorityMap.get(packagePath) ?? 0;
        // Lower priority of this package path by 1.
        priorityMap.set(packagePath, priority - 1);

        // Use the sequential executor to run the evaluation to avoid concurrency issues.
        executor
          .execute(packagePath, () =>
            // Evaluate this path, but don't await - when we're done, we'll re-enqueue newly discovered paths.
            evaluatePath(
              {
                ...options,
                packagePath,
                importerContext,
                // No need to force if we're in a multi-app scenario
                // as we'll re-evaluate packages if they are different since we use a different task id.
                shouldForce: !isMultiApp && shouldForce,
                priority,
              },
              context,
            ),
          )
          .then(({ pathsToEvaluate, pathsToRefresh, shouldExit }) => {
            pending--;

            if (shouldExit) {
              isExitingPrematurely = true;
            } else if (pathsToEvaluate?.size) {
              // If there are packages that need to be rebuilt, delete them from the visited set to re-enqueue them
              // and add them to the refresh set so that on re-enqueuing we pass the `shouldRerun` flag through
              // on single app runs.
              pathsToRefresh?.forEach((refreshPath) => {
                refreshPaths.add(refreshPath);
                visitedSet.delete(refreshPath);
              });

              // Try to enqueue the new paths.
              console.debug(
                `Enqueuing paths found from ${packagePath}:\n` +
                  bulletedList(
                    [...pathsToEvaluate].map(([newPath, newContext]) => `${newPath} ${JSON.stringify(newContext)}`),
                  ),
              );
              enqueuePaths(pathsToEvaluate);
            }

            // If nothing is pending after this call, we're complete.
            if (!pending) {
              resolve();
            }
          })
          .catch((error: unknown) => {
            // Record the exception as an init error for the package.
            // This gives better context than a stack trace which could have come from any package.
            let prefix = 'evaluating package';
            if (importerContext) {
              prefix += ` (context: ${JSON.stringify(importerContext, null, 2).replace(/\s+/g, ' ')})`;
            }
            summaryData.recordException({ packagePath, error, source: 'init', context: prefix });

            // Don't immediately stop evaluation, but don't enqueue more paths.
            isExitingPrematurely = true;
          });
      }
    }
  }).catch((error: unknown) => {
    // If there's an error thrown from some other part of the promise besides evaluatePath (not likely),
    // record it as an init error for the app instead of allowing it to propagate.
    // This allows the overall init summary and any other errors to be logged as usual.
    isExitingPrematurely = true;
    summaryData.recordException({ packagePath: appPath, error, source: 'init', context: 'running init' });
  });

  // Clear the executor so that it doesn't hold onto any references to the promises.
  executor.clear();

  // Log the resolve map to a file if requested. Do this *after* the main init process in case
  // any additional deps were discovered and added.
  if (initOptions.logResolveMap) {
    console.log('\nWriting resolve map to `resolve-map.json`.');
    await writeJson(path.join(appPath, 'resolve-map.json'), resolveMap);
  }

  if (isExitingPrematurely) {
    span.setAttribute('isExitingPrematurely', true);
  } else {
    // Only do extra checks if there's not a major error already
    if (config.features?.optimizeDependencies) {
      // Conditionally scan dependency usage so that we can populate
      // include/excludeDependencies automatically, ensuring the dep list
      // is both minimal (remove unused deps) and complete (include
      // specific devDeps.)
      const excludedDependenciesChanges = await optimizeDependencies(summaryData.getResults(), context);
      summaryData.recordGeneratedChanges(excludedDependenciesChanges);
    }

    if (config.features?.verifyExports) {
      const packageResults = await verifyExports(summaryData.getResults(), context);
      for (const [packagePath, errors] of Object.entries(packageResults)) {
        summaryData.addErrors(packagePath, errors);
      }
    }
  }

  // The feature "removeUnusedExports" doesn't (yet) write summary changes, but we still
  // want to write the generated config if that featureFlag is on.
  const hasSummaryChanges = config.features?.removeUnusedExports || summaryData.hasChanges();

  if (!initOptions.check && !options.skipWrite && hasSummaryChanges) {
    // Update the generated config: either write it if there are settings, or delete it if it
    // previously existed but is no longer needed.
    const generatedResult = await writeGeneratedConfig(config.generated, appPath);
    if (generatedResult === 'deleted') {
      summaryData.deletedGeneratedConfig = true;
    }
  }
}
